/**
 * Safely observe element size for Lightweight Charts.
 * Calling chart.resize / applyOptions inside ResizeObserver synchronously
 * triggers "ResizeObserver loop completed with undelivered notifications"
 * in React's CRA error overlay — defer to the next animation frame.
 */
export function observeChartResize(
  element: HTMLElement,
  onSize: (width: number, height: number) => void
): () => void {
  let frameId = 0;
  let lastWidth = -1;
  let lastHeight = -1;

  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (!entry) return;

    const { width, height } = entry.contentRect;
    if (width <= 0 || height <= 0) return;
    if (width === lastWidth && height === lastHeight) return;

    cancelAnimationFrame(frameId);
    frameId = requestAnimationFrame(() => {
      lastWidth = width;
      lastHeight = height;
      onSize(width, height);
    });
  });

  observer.observe(element);

  return () => {
    cancelAnimationFrame(frameId);
    observer.disconnect();
  };
}
